/**
 * Edit form for an assessment.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { AssessmentDetailsPanel } from '@/components/assessments/renderAssessmentDetails';
import { getAssessment } from '@/services/assessments.service';
import { ASSESSMENT_FORM_QUERY_KEYS } from './assessment-configs';
import { buildEditAssessmentSubtitle, buildEditAssessmentTitle } from './assessment-form-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';

/** Props for {@link EditAssessmentView}. Wraps {@link AssessmentDetailsPanel} (footer hidden). */
interface EditAssessmentViewProps {
  assessmentId: string;
  onClose: () => void;
}

/** Edit form for an assessment. */
export const EditAssessmentView: React.FC<EditAssessmentViewProps> = ({
  assessmentId,
  onClose,
}) => {
  const {
    data: assessment,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ASSESSMENT_FORM_QUERY_KEYS.one(assessmentId),
    queryFn: () => getAssessment(assessmentId),
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'assessmentNotFound',
    onClose,
    entityId: assessmentId,
  });

  let content: React.ReactNode = null;
  if (assessment) {
    content = (
      <AssessmentDetailsPanel
        assessment={assessment}
        presentationMode="edit"
        hideDetailHeader
        onActionSuccess={onClose}
      />
    );
  }

  return (
    <CollapseReplaceForm
      formType="edit"
      title={buildEditAssessmentTitle(assessment)}
      subtitle={buildEditAssessmentSubtitle(assessment)}
      onClose={onClose}
      onSubmit={() => {}}
      submitLabel="Enregistrer"
      isSubmitting={false}
      isValid
      isLoading={isLoading}
      showFooter={false}
    >
      {content}
    </CollapseReplaceForm>
  );
};
